import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  Button,
  Alert,
  Stack,
  CircularProgress,
  LinearProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Refresh, Close, AccountBalance, NotificationsActive } from '@mui/icons-material';
import LoadingButton from '../components/LoadingButton';
import { checksApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { formatMoney } from '../utils/money';
import { formatDate } from '../utils/helpers';

const ALERT_KINDS = {
  duplicate_charge: { label: 'Charged twice?', color: 'warning' },
  unusual_amount: { label: 'Unusual amount', color: 'info' },
  fee: { label: 'Fee', color: 'error' },
  interest: { label: 'Interest', color: 'error' },
  foreign_fee: { label: 'Foreign fee', color: 'warning' },
  balance_gap: { label: 'Balance gap', color: 'warning' },
};

// How many gaps to list per account before "show all"
const GAPS_SHOWN = 6;

/**
 * Checks: what the app found by reconciling your money. Alerts (charged
 * twice, unusual amounts, fees, interest) and balance checks (does each
 * account's balance move by exactly what was recorded?).
 */
const Checks = () => {
  const { showSuccess, showError } = useNotification();
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    try {
      const response = await checksApi.get();
      setData(response.data);
    } catch (error) {
      showError('Failed to load checks');
      setData({ alerts: [], balances: [] });
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setScanning(true);
    try {
      const response = await checksApi.scan();
      showSuccess(response.data.raised ? `${response.data.raised} new thing${response.data.raised === 1 ? '' : 's'} to look at` : 'Nothing new found');
      await load();
    } catch (error) {
      showError('Failed to check transactions');
    } finally {
      setScanning(false);
    }
  };

  const dismiss = async (alert) => {
    try {
      await checksApi.dismiss(alert.id);
      setData(prev => ({ ...prev, alerts: prev.alerts.filter(a => a.id !== alert.id) }));
    } catch (error) {
      showError('Failed to dismiss');
    }
  };

  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h4">Checks</Typography>
        <LoadingButton variant="outlined" startIcon={<Refresh />} loading={scanning} onClick={scan}>
          Check again
        </LoadingButton>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        What PinePocket found by checking your transactions against each other and against your account balances.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardHeader avatar={<NotificationsActive color="primary" />} title="To look at" subheader={`${data.alerts.length} open`} />
        <CardContent sx={{ pt: 0 }}>
          {data.alerts.length === 0 ? (
            <Alert severity="info">Nothing to look at. Double charges, unusual amounts, fees and interest show up here.</Alert>
          ) : (
            <List disablePadding>
              {data.alerts.map((alert, index) => (
                <React.Fragment key={alert.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem disableGutters sx={{ pr: 6 }}>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip size="small" variant="outlined" color={ALERT_KINDS[alert.kind]?.color || 'default'} label={ALERT_KINDS[alert.kind]?.label || alert.kind} />
                          <Typography variant="body2" fontWeight={500}>{alert.title}</Typography>
                        </Stack>
                      }
                      secondary={alert.message}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Dismiss">
                        <IconButton edge="end" size="small" onClick={() => dismiss(alert)}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<AccountBalance color="primary" />}
          title="Balance checks"
          subheader="Each balance in your bank alerts should move by exactly the transactions recorded since the last one. Last 4 months."
        />
        <CardContent sx={{ pt: 0 }}>
          {data.balances.length === 0 ? (
            <Alert severity="info">No balances yet. Bank alerts that state your balance make this check possible.</Alert>
          ) : (
            <Stack spacing={3}>
              {data.balances.map(account => {
                const share = Math.round((100 * (account.explained + account.timing)) / Math.max(1, account.pairs));
                const cameIn = account.gaps.filter(g => g.direction === 'in');
                const wentOut = account.gaps.filter(g => g.direction === 'out');
                const shown = expanded[account.accountId] ? account.gaps : account.gaps.slice(-GAPS_SHOWN);
                return (
                  <Box key={account.accountId}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1">{account.name}</Typography>
                      <Typography variant="body2" color={share >= 90 ? 'success.main' : 'warning.main'} fontWeight={600}>
                        {share}% of {account.pairs} days add up
                      </Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={share} color={share >= 90 ? 'success' : 'warning'} sx={{ my: 1, height: 6, borderRadius: 3 }} />
                    {account.gaps.length > 0 && (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {cameIn.length > 0 && `${cameIn.length} time${cameIn.length === 1 ? '' : 's'} more came in than was recorded (${formatMoney(cameIn.reduce((s, g) => s + g.amount, 0))})`}
                          {cameIn.length > 0 && wentOut.length > 0 && '; '}
                          {wentOut.length > 0 && `${wentOut.length} time${wentOut.length === 1 ? '' : 's'} more went out (${formatMoney(wentOut.reduce((s, g) => s + g.amount, 0))})`}
                          {'. Usually an alert that never arrived: check that your forwarding includes credit alerts.'}
                        </Typography>
                        <List dense disablePadding>
                          {shown.map(gap => (
                            <ListItem key={`${gap.from}-${gap.to}`} disableGutters>
                              <ListItemText
                                primary={`${formatDate(gap.from)} – ${formatDate(gap.to)}`}
                                secondary={gap.direction === 'in' ? 'came in without a recorded transaction' : 'went out without a recorded transaction'}
                              />
                              <Typography variant="body2" color={gap.direction === 'in' ? 'success.main' : 'error.main'}>
                                {gap.direction === 'in' ? '+' : '−'}{formatMoney(gap.amount)}
                              </Typography>
                            </ListItem>
                          ))}
                        </List>
                        {account.gaps.length > GAPS_SHOWN && (
                          <Button size="small" onClick={() => setExpanded(prev => ({ ...prev, [account.accountId]: !prev[account.accountId] }))}>
                            {expanded[account.accountId] ? 'Show fewer' : `Show all ${account.gaps.length}`}
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

    </Box>
  );
};

export default Checks;
